import { useState, useEffect, useCallback } from 'react';
import { loadAllShippingAgents } from '../services/ShippingAgentService';
import type { ShippingAgent } from '../types/Procurement';

interface UseShippingAgentsResult {
  /** Active agents (the ones a picker offers); `allAgents` also holds switched-off ones. */
  agents: ShippingAgent[];
  allAgents: ShippingAgent[];
  loading: boolean;
  /** The caught error, for a LoadErrorBox; null when the load worked. */
  error: unknown;
  reload: () => Promise<void>;
}

/**
 * Loads the shipping agents (with rates and fees) from the planning API. A
 * failed load is kept in `error` (never an empty list): the screen shows an
 * error box with Retry. Pass `{ silent: true }` semantics through `reload`
 * callers that refresh in the background: they ignore the rejection.
 */
export function useShippingAgents(): UseShippingAgentsResult {
  const [allAgents, setAllAgents] = useState<ShippingAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAllAgents(await loadAllShippingAgents());
    } catch (err) {
      setError(err ?? new Error('load failed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return { agents: allAgents.filter(a => a.active), allAgents, loading, error, reload };
}
